import React, { useEffect, useState, useMemo } from 'react';
import { formatDate } from '../../services';
import { TablePagination } from '../../components/TablePagination';
import { TableStub } from '../../components/TableStub';
import { useDispatch, useSelector } from 'react-redux';
import {
  fillContactFormAction,
  getApiAssessment,
  setAssessmentCurrentPageAction,
  setAssessmentItemsPerPageAction,
  sortAssessmentByAlphabetAction,
  sortAssessmentByEndaDateAction,
  sortAssessmentByStartDateAction,
  getApiUnits,
} from 'store/assessment/actions';
import { IUnitSystem } from 'store/assessment/types';
import TableLoader from 'components/TableLoader';
import { IAppState } from 'store';
import 'assets/css/Table.scss';
import './AssessmentPage.scss';
import { NavLink } from 'react-router-dom';
import { TableFilter } from 'components/TableFilter';
import { useNotifications } from '../../hooks/useNotification';
import { NotificationsTypes } from '../../store/notifications/types';
import { Dropdown } from 'react-bootstrap';
import clsx from 'clsx';
import Api from '../../api';
import { downloadUrl, getPermissions } from './helpers';
import IconMark from '../../assets/icons/IconMark';
import IconCopy from '../../assets/icons/IconCopy';
import IconDownload from '../../assets/icons/IconDownload';
import IconUpload from '../../assets/icons/IconUpload';
import { useHistory } from 'react-router';
import { ROUTES } from '../../config/routes';
import IconReload from '../../assets/icons/IconReload';
import { SuccessModal } from '../../components/SuccessModal';
import settingsSuccess from '../../assets/images/ic-state-success.svg';
import attention from '../../assets/images/00-icons-toast-ic-attantion.svg';
import { useTranslation } from 'react-i18next';
import IconRejected from '../../assets/icons/IconRejected';

/**
 * Process translations: remove duplicates by LanguageCode and sort (English variants first, then alphabetically)
 */
const processTranslations = (translations: any[]) => {
  if (!translations || translations.length === 0) return [];

  // Remove duplicates by LanguageCode
  const uniqueMap = new Map<string, any>();
  translations.forEach(t => {
    if (!uniqueMap.has(t.LanguageCode)) {
      uniqueMap.set(t.LanguageCode, t);
    }
  });

  const unique = Array.from(uniqueMap.values());

  // Sort: English variants first, then alphabetically
  return unique.sort((a, b) => {
    const aIsEnglish = a.LanguageName?.toLowerCase().startsWith('english') || false;
    const bIsEnglish = b.LanguageName?.toLowerCase().startsWith('english') || false;

    // English variants go first
    if (aIsEnglish && !bIsEnglish) return -1;
    if (!aIsEnglish && bIsEnglish) return 1;

    // Alphabetical order within each group
    return (a.LanguageName || '').localeCompare(b.LanguageName || '');
  });
};

export const AssessmentPage = () => {
  useNotifications(NotificationsTypes.assessments);
  const [showModal, setShowModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showBEError, setShowBEError] = useState(false);
  const [showCompletedUnitWarning, setShowCompletedUnitWarning] = useState(false);
  const [completedAssessmentOpen, setCompletedAssessmentOpen] = useState<{
    id: number;
    languageCode?: string;
    openInNewTab?: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedUnitSystemId, setSelectedUnitSystemId] = useState<number | null>(null);
  const [openLanguageDropdownId, setOpenLanguageDropdownId] = useState<number | null>(null);
  const { t } = useTranslation();

  const ASSESSMENTS_STATUSES = [
    t('AP_new'),
    t('AP_in_progress'),
    t('AP_complet'),
    'Reallocated',
    'Downloaded',
    t('AP_updated'),
    'Autogenerated',
    'Obsolete',
    t('AP_uploaded'),
  ];

  const ASSESSMENTS_REQUEST_STATUSES = [
    t('No Request Made'),
    t('Request Pending'),
    t('Request Rejected'),
    t('Request Approved'),
  ];

  const ASSESSMENTS_FILE_STATUSES = [null, t('File Uploaded')];

  const dispatch = useDispatch();
  const history = useHistory();
  const {
    assessmentItems,
    displayItems,
    itemsPerPage,
    totalPagesNumber,
    isLoading,
    currentPageNumber,
    sortedByStartDate,
    sortedByEndDate,
    sortedByAlphabet,
    units,
    isLoadingUnits,
  } = useSelector((state: IAppState) => state.assessment);

  useEffect(() => {
    dispatch(getApiAssessment());
    dispatch(getApiUnits());
  }, [dispatch]);

  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as Element;
      if (target?.closest?.('.language-dropdown-menu')) return;
      setOpenLanguageDropdownId(null);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  useEffect(() => {
    if (!units || units.length === 0) return;

    const saved = localStorage.getItem('selectedUnitSystemId');
    const parsed = saved ? Number(saved) : null;
    const hasSaved = parsed !== null && !isNaN(parsed);
    const matchSaved = hasSaved ? units.find(u => Number(u.ID) === parsed) : null;

    if (matchSaved) {
      setSelectedUnitSystemId(matchSaved.ID);
      return;
    }

    const metricByName = units.find(
      u =>
        u.Name?.toLowerCase().includes('metric') ||
        u.Name?.toLowerCase().includes('si')
    );
    const fallback = metricByName || units[0] || null;

    if (fallback) {
      setSelectedUnitSystemId(fallback.ID);
      localStorage.setItem('selectedUnitSystemId', String(fallback.ID));
    } else {
      setSelectedUnitSystemId(null);
      localStorage.removeItem('selectedUnitSystemId');
    }
  }, [units]);

  const { metricSystem, imperialSystem } = useMemo(() => {
    let metric: IUnitSystem | null = null;
    let imperial: IUnitSystem | null = null;

    units.forEach(system => {
      const name = system.Name?.toLowerCase() || '';
      if (name.includes('metric')) {
        metric = system;
      } else if (name.includes('imperial')) {
        imperial = system;
      }
    });

    if (!metric && units.length > 0) {
      metric = units[0];
    }
    if (!imperial && units.length > 1) {
      imperial = units.find(s => s.ID !== metric?.ID) || units[1];
    }

    return {
      metricSystem: metric,
      imperialSystem: imperial,
    };
  }, [units]);

  const selectedSystemName = useMemo(() => {
    if (!selectedUnitSystemId) {
      return t('Unit Settings') || 'Unit Settings';
    }
    
    const selectedId = Number(selectedUnitSystemId);

    if (metricSystem && Number(metricSystem.ID) === selectedId) {
      return t('unit_system_metric');
    }

    if (imperialSystem && Number(imperialSystem.ID) === selectedId) {
      return t('unit_system_imperial');
    }

    return t('unit_settings');
  }, [selectedUnitSystemId, metricSystem, imperialSystem, t]);

  const downloadTemplate = (id, status) => {
    if (status != 3) return null;
    setLoading(true);
    Api(false)
      .get(`/api/Upload/GetUploadTemplate?assessmentID=${id}`, {
        responseType: 'blob',
      })
      .then(response => {
        downloadUrl(
          window.URL.createObjectURL(new Blob([response.data])),
          'template.xlsx',
        );
      })
      .then(response => {
        setLoading(false);
      });
  };

  const uploadFile = async (file, id) => {
    let formData = new FormData();
    if (file.name.split('.').pop() !== 'xlsx') {
      setShowErrorModal(true);
      return;
    }

    formData.append('files', file);
    formData.append('targetAssessmentID', id);
    setLoading(true);

    Api(false)
      .post(`/api/Upload/UploadTemplate`, formData, {
        headers: {
          'Content-type': 'multipart/form-data',
        },
      })
      .then(response => {
        dispatch(getApiAssessment());
        // console.log(response);
        setLoading(false);
        setShowModal(true);
      })
      .catch(error => {
        setLoading(false);
        setShowBEError(true);
      });
  };

  const changeItemsPerPage = (count: number) => {
    dispatch(setAssessmentItemsPerPageAction(count));
  };

  const changeCurrentPage = (pageNumber: number) => {
    dispatch(setAssessmentCurrentPageAction(pageNumber));
  };

  const handleSortByAlphabet = () => {
    dispatch(sortAssessmentByAlphabetAction(!sortedByAlphabet));
  };

  const handleSortByStartDate = () => {
    dispatch(sortAssessmentByStartDateAction(!sortedByStartDate));
  };

  const handleSortByEndDate = () => {
    dispatch(sortAssessmentByEndaDateAction(!sortedByEndDate));
  };

  const getPermissionsAccess = async (id, status) => {
    if (status !== 0) return;
    setShowStatusModal(true);
    await getPermissions(id).then(resp => dispatch(getApiAssessment()));
    // history.push(ROUTES.contact);
    //dispatch(fillContactFormAction(true))
  };

  const handleLanguageSelect = (languageCode, item) => {
    openAssessment(item, { languageCode, openInNewTab: false });
  };

  const hiddenFileInput = React.useRef(null);

  const handleClick = (e, status, requestStatus) => {
    if (requestStatus !== 3) return;
    //if (status != -1 || requestStatus == 5) return;
    //if ((status === -1 || status === 5) && requestStatus === 3) {
    if (status === -1 || status === 2 || status === 4) {
      hiddenFileInput.current.click();
    }
  };
  const handleChange = (event, id) => {
    const fileUploaded = event.target.files[0];
    uploadFile(fileUploaded, id);
  };

  // @ts-ignore
  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <a
      href=""
      // @ts-ignore
      ref={ref}
      onClick={e => {
        e.preventDefault();
        onClick(e);
      }}
    >
      {children}
      <span className="threedots" />
    </a>
  ));

  // Navigate to the assessment, applying langTemp side effect.
  const proceedOpen = (
    item,
    options?: { languageCode?: string; openInNewTab?: boolean },
  ) => {
    const { languageCode, openInNewTab } = options || {};

    if (languageCode) {
      localStorage.setItem('langTemp', languageCode);
    } else {
      localStorage.removeItem('langTemp');
    }

    const url = `/dashboard/assessments/${item.ID}`;

    if (openInNewTab) {
      window.open(url, '_blank');
    } else {
      history.push(url);
    }
  };

  const openAssessment = (
    item,
    options?: { languageCode?: string; openInNewTab?: boolean },
  ) => {
    const { languageCode, openInNewTab } = options || {};

    // Completed assessments (statuses 2 and 5) keep their own info modal.
    if (item?.StatusFlag === 2 || item?.StatusFlag === 5) {
      if (languageCode) {
        localStorage.setItem('langTemp', languageCode);
      } else {
        localStorage.removeItem('langTemp');
      }
      setCompletedAssessmentOpen({
        id: item.ID,
        languageCode,
        openInNewTab,
      });
      setShowCompletedUnitWarning(true);
      return;
    }

    proceedOpen(item, { languageCode, openInNewTab });
  };

  const handleCompletedUnitWarningClose = () => {
    if (completedAssessmentOpen) {
      const { id, languageCode, openInNewTab } = completedAssessmentOpen;

      if (languageCode) {
        localStorage.setItem('langTemp', languageCode);
      } else {
        localStorage.removeItem('langTemp');
      }

      const url = `/dashboard/assessments/${id}`;
      if (openInNewTab) {
        window.open(url, '_blank');
      } else {
        history.push(url);
      }
    }
    setCompletedAssessmentOpen(null);
  };

  return (
    <div className="assessment-content">
      <div className="container-custom container">
        <div className="row">
          <div className="col-12">
            <div className="header-wrapper">
              <h2 className="table-heading">{t('AP_assesments')}</h2>
              <div className="header-actions">
                <Dropdown>
                  <Dropdown.Toggle
                    as="button"
                    className="btn action-secondary unit-settings-btn"
                    disabled={units.length === 0}
                  >
                    {selectedSystemName}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="unit-settings-dropdown-menu">
                    {metricSystem && (
                      <Dropdown.Item
                        className="unit-settings-dropdown-item"
                        onClick={() => {
                          setSelectedUnitSystemId(metricSystem.ID);
                          localStorage.setItem('selectedUnitSystemId', String(metricSystem.ID));
                        }}
                        active={Number(selectedUnitSystemId) === Number(metricSystem.ID)}
                      >
                        <span className="unit-option-name">{t('unit_system_metric')}</span>
                      </Dropdown.Item>
                    )}
                    {imperialSystem && (
                      <Dropdown.Item
                        className="unit-settings-dropdown-item"
                        onClick={() => {
                          setSelectedUnitSystemId(imperialSystem.ID);
                          localStorage.setItem('selectedUnitSystemId', String(imperialSystem.ID));
                        }}
                        active={Number(selectedUnitSystemId) === Number(imperialSystem.ID)}
                      >
                        <span className="unit-option-name">{t('unit_system_imperial')}</span>
                      </Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
                <NavLink
                  to={`/dashboard/createAssessments`}
                  className="action-primary btn table-action-btn"
                >
                  {t('Create New Assessment')}
                </NavLink>
              </div>
            </div>
            <div className="custom-table assessments-table">
              <div className="table-header">
                <div className="assessment-wrapper">
                  <TableFilter
                    active={sortedByAlphabet}
                    text={t('AP_assesmnet')}
                    onSort={handleSortByAlphabet}
                  />
                </div>
                <div className="description-wrapper">
                  <span>{t('AP_desc')}</span>
                </div>
                <div className="date-created-wrapper">
                  <TableFilter
                    active={sortedByStartDate}
                    text={t('AP_date_cr')}
                    onSort={handleSortByStartDate}
                  />
                </div>
                <div className="date-completed-wrapper">
                  <TableFilter
                    active={sortedByEndDate}
                    text={t('AP_date_compl')}
                    onSort={handleSortByEndDate}
                  />
                </div>
                <div className="status-wrapper">
                  <span>{t('AP_status')}</span>
                </div>
                <div className="action-wrapper">
                  <span>{t('AP_action')}</span>
                </div>
              </div>
              {isLoading || loading ? (
                <TableLoader />
              ) : displayItems.length ? (
                <>
                  <div className="table-body">
                    {displayItems.map(item => {
                      const singleAssessment =
                        item.Package === null ? true : false;
                      const hasTranslations = item.Translations && item.Translations.length > 0;
                      const primaryPackageAssessment =
                        !singleAssessment &&
                        item.Template_ID ===
                          item.Package.PackageTemplate.PrimaryTemplateID
                          ? true
                          : false;

                      return (
                        <div className="table-item" key={item.ID}>
                          <div className="assessment-wrapper">
                            <span>
                              {primaryPackageAssessment
                                ? item.Package.PackageTemplate.Name
                                : item.TemplateName}
                            </span>
                          </div>
                          <div className="description-wrapper">
                            <span>{item.Description}</span>
                          </div>
                          <div className="date-created-wrapper">
                            <span>
                              {formatDate(item.StartDate, 'MMMM D, YYYY')}
                            </span>
                          </div>
                          <div className="date-completed-wrapper">
                            <span>
                              {formatDate(item.EndDate, 'MMMM D, YYYY')}
                            </span>
                          </div>
                          <div className="status-wrapper">
                            <span>{ASSESSMENTS_STATUSES[item.StatusFlag]}</span>
                          </div>
                          <div className="action-wrapper">




                            {hasTranslations ? (
                                <Dropdown
                                  show={openLanguageDropdownId === item.ID}
                                  onToggle={isOpen => setOpenLanguageDropdownId(isOpen ? item.ID : null)}
                                >
                                  <Dropdown.Toggle
                                      as="div"
                                      className="action-primary btn table-action-btn"
                                      style={{ cursor: 'pointer' }}
                                  >
                                    {t('AP_open')}
                                  </Dropdown.Toggle>

                                  <Dropdown.Menu className="language-dropdown-menu">
                                    <Dropdown.Item onClick={() => openAssessment(item)}>
                                      {t('AP_default_language')}
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    {processTranslations(item.Translations).map((translation, index, array) => (
                                        <React.Fragment key={translation.LanguageCode}>
                                          <Dropdown.Item
                                              onClick={() => handleLanguageSelect(translation.LanguageCode, item)}
                                          >
                                            {translation.LanguageName}
                                          </Dropdown.Item>
                                          {index !== array.length - 1 && (
                                              <Dropdown.Divider />
                                          )}
                                        </React.Fragment>
                                    ))}
                                  </Dropdown.Menu>
                                </Dropdown>
                            ) : (
                                <NavLink
                                  to={`/dashboard/assessments/${item.ID}`}
                                  className="action-primary btn table-action-btn"
                                  onClick={e => {
                                    e.preventDefault();
                                    openAssessment(item);
                                  }}
                                >
                                  {t('AP_open')}
                                </NavLink>
                            )}




                            <div>
                              {item?.StatusFlag === 2 ? (
                                <IconMark color="green" />
                              ) : (
                                <Dropdown>
                                  <Dropdown.Toggle as={CustomToggle} />
                                  <Dropdown.Menu
                                    title=""
                                    className="bulk-upload-dropdown-menu"
                                  >
                                    <Dropdown.Item
                                      as="div"
                                      onClick={() =>
                                        getPermissionsAccess(
                                          item.ID,
                                          item?.RequestState,
                                        )
                                      }
                                      className={clsx({
                                        'cursor-active':
                                          ASSESSMENTS_REQUEST_STATUSES[
                                            item?.RequestState
                                          ] === 'No Request Made',
                                      })}
                                    >
                                      <div className="d-flex">
                                        <IconCopy
                                          color={
                                            ASSESSMENTS_REQUEST_STATUSES[
                                              item?.RequestState
                                            ] === 'Request Approved'
                                              ? '#d0d0d1'
                                              : 'black'
                                          }
                                        />
                                        <div
                                          className={clsx(
                                            'asking-for-permission-active',
                                            {
                                              'cursor-active':
                                                ASSESSMENTS_REQUEST_STATUSES[
                                                  item?.RequestState
                                                ] === 'No Request Made',
                                              disabled:
                                                ASSESSMENTS_REQUEST_STATUSES[
                                                  item?.RequestState
                                                ] === 'Request Approved',
                                            },
                                          )}
                                        >
                                          {t('Request Bulk Upload')}
                                        </div>
                                        {/* <IconMark
                                    color={ASSESSMENTS_REQUEST_STATUSES[item?.RequestState] === 'No Request Made' ? '#d0d0d1' : 'green'} /> */}
                                        {item?.RequestState === 1 ? (
                                          <IconReload
                                            color={
                                              ASSESSMENTS_REQUEST_STATUSES[
                                                item?.RequestState
                                              ] === 'No Request Made'
                                                ? '#d0d0d1'
                                                : 'green'
                                            }
                                          />
                                        ) : item?.RequestState === 2 ? (
                                          <IconRejected />
                                        ) : (
                                          <IconMark
                                            color={
                                              ASSESSMENTS_REQUEST_STATUSES[
                                                item?.RequestState
                                              ] === 'No Request Made'
                                                ? '#d0d0d1'
                                                : 'green'
                                            }
                                          />
                                        )}
                                      </div>
                                      <div
                                        className={clsx('request-approved', {
                                          active:
                                            ASSESSMENTS_REQUEST_STATUSES[
                                              item?.RequestState
                                            ] === 'Request Approved',
                                        })}
                                      >
                                        {
                                          ASSESSMENTS_REQUEST_STATUSES[
                                            item?.RequestState
                                          ]
                                        }
                                      </div>
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item
                                      as="div"
                                      onClick={() =>
                                        downloadTemplate(
                                          item.ID,
                                          item?.RequestState,
                                        )
                                      }
                                    >
                                      <div className="d-flex">
                                        <IconDownload
                                          color={
                                            ASSESSMENTS_REQUEST_STATUSES[
                                              item?.RequestState
                                            ] !== 'Request Approved'
                                              ? '#d0d0d1'
                                              : 'black'
                                          }
                                        />
                                        <div
                                          className={clsx(
                                            'asking-for-permission-active',
                                            {
                                              'cursor-active disabled':
                                                ASSESSMENTS_REQUEST_STATUSES[
                                                  item?.RequestState
                                                ] !== 'Request Approved',
                                            },
                                            {
                                              'cursor-active':
                                                ASSESSMENTS_REQUEST_STATUSES[
                                                  item?.RequestState
                                                ] === 'Request Approved',
                                            },
                                          )}
                                        >
                                          {t('Download Template')}
                                        </div>
                                      </div>
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item
                                      as="div"
                                      onClick={e =>
                                        handleClick(
                                          e,
                                          item.FileState,
                                          item?.RequestState,
                                        )
                                      }
                                    >
                                      <div className="d-flex">
                                        <IconUpload
                                          color={
                                            ASSESSMENTS_REQUEST_STATUSES[
                                              item?.RequestState
                                            ] !== 'Request Approved'
                                              ? '#d0d0d1'
                                              : 'black'
                                          }
                                        />
                                        <div
                                          className={clsx(
                                            'asking-for-permission-active',
                                            {
                                              disabled:
                                                ASSESSMENTS_REQUEST_STATUSES[
                                                  item?.RequestState
                                                ] !== 'Request Approved' ||
                                                ASSESSMENTS_REQUEST_STATUSES[
                                                  item?.RequestState
                                                ] !== 'Request Rejected',
                                            },
                                            {
                                              'cursor-active':
                                                ASSESSMENTS_REQUEST_STATUSES[
                                                  item?.RequestState
                                                ] === 'Request Approved',
                                            },
                                          )}
                                        >
                                          {t('Upload File')}
                                        </div>
                                        <input
                                          type="file"
                                          ref={hiddenFileInput}
                                          onChange={e =>
                                            handleChange(e, item.ID)
                                          }
                                          style={{ display: 'none' }}
                                          accept=".xlsx"
                                        />
                                        {/* {item?.FileState === 1 ? <IconReload color="green" /> : item?.FileState === 4 ? <IconRejected /> : <IconMark color="#d0d0d1" />} */}
                                        {item?.FileState === 0 && (
                                          <IconMark color="green" />
                                        )}
                                        {item?.FileState === 1 && (
                                          <IconMark color="#d0d0d1" />
                                        )}
                                        {item?.FileState === 2 && (
                                          <IconReload color="green" />
                                        )}
                                        {item?.FileState === 3 && (
                                          <IconMark color="green" />
                                        )}
                                        {item?.FileState === 4 ||
                                          (item?.FileState === 5 && (
                                            <IconRejected />
                                          ))}
                                      </div>
                                      {item?.FileStateText && (
                                        <span
                                          className="request-approved"
                                          dangerouslySetInnerHTML={{
                                            __html: t(item.FileStateText),
                                          }}
                                        ></span>
                                      )}
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <TableStub description={t('AP_view')} />
              )}
              <TablePagination
                pagesCount={totalPagesNumber}
                itemsPerPage={itemsPerPage}
                activePageNumber={currentPageNumber}
                changeItemsPerPageCount={changeItemsPerPage}
                changePage={changeCurrentPage}
                totalItems={assessmentItems.length}
              />
            </div>
          </div>
        </div>
      </div>
      <SuccessModal
        imgUrl={settingsSuccess}
        show={showModal}
        handleShowModal={setShowModal}
        modalText={'File has been uploaded successfully'}
      />
      <SuccessModal
        imgUrl={settingsSuccess}
        show={showStatusModal}
        handleShowModal={setShowStatusModal}
        modalText={t('request_modal')}
      />
      <SuccessModal
        imgUrl={attention}
        show={showBEError}
        handleShowModal={setShowBEError}
        modalText={t('assessment_page_error_try_again')}
      />
      <SuccessModal
        imgUrl={attention}
        show={showErrorModal}
        handleShowModal={setShowErrorModal}
        modalText={t('assessment_page_invalid_file_format')}
      />
      <SuccessModal
        imgUrl={attention}
        show={showCompletedUnitWarning}
        handleShowModal={setShowCompletedUnitWarning}
        modalText={t('assessment_page_completed_assessment_title')}
        description={t('assessment_page_completed_assessment_description')}
        onClose={handleCompletedUnitWarningClose}
      />
    </div>
  );
};
